import React from 'react';

import { useAppContext } from 'src/components/app-context';

import { BadgeSize } from '@prisjakt/ui/badge';
import { IconLeaf } from '@prisjakt/ui/icon/leaf';
import {
  BodySmallText,
  FontEmphasis,
  H4Text,
  TitleSmallText,
} from '@prisjakt/ui/text/variants';
import { CarrierLogo } from 'src/areas/product/components/carrier-logos/carrier-logos';
import { Currency } from 'src/components/currency';

import { getDeliveryMethodsConfig } from 'src/areas/product/components/price-list/offer-list-filters/shipping-filters/delivery-methods/utils';
import { getTranslatedShipping } from '../../delivery-time/utils';

import { Shipping } from 'src/types';
import { SustainabilityEnum } from 'src/components/price-list/types';

import {
  ShippingOptionList,
  ShippingOptionItem,
  ItemTitle,
  StyledBadge,
  StyledCaptionText,
  StyledSustainableDescription,
  TitleWrapper,
} from './styles';
import { ExtendedPrice } from 'src/areas/product/components/product-context';

type DeliveryOptionsProps = {
  offer: ExtendedPrice;
};

export const DeliveryOptions = ({ offer }: DeliveryOptionsProps) => {
  const { market, t } = useAppContext();
  const missingInfoText = t(
    'product',
    'delivery.missing-shipping-information',
    {
      store: offer.store?.name || '',
    }
  );

  const deliveryMethodTranslation = (optionMethod: string) => {
    const deliveryOption = getDeliveryMethodsConfig().find(
      option => option.type === optionMethod
    );

    const key = deliveryOption?.translationString;

    if (!key) {
      return null;
    }

    // @ts-ignore
    return t('product', key) as string;
  };

  const getDeliveryInfo = (node: Shipping) => {
    if (!node?.deliveryMethod) {
      return missingInfoText;
    }

    const carrierText = node?.carrier ? ` • ${node.carrier}` : '';

    return `${deliveryMethodTranslation(
      node?.deliveryMethod || ''
    )}${carrierText}`;
  };

  const renderShippingOptions = () => {
    const nodes = offer.unfilteredShippings
      ?.filter(
        node => node?.carrier || node?.deliveryMethod === 'INSTORE_PICKUP'
      )
      .sort((a, b) => {
        const aCarrier: string = a.carrier || '';
        const bCarrier: string = b.carrier || '';
        const aShippingCost: number = a?.shippingCost || 0;
        const bShippingCost: number = b?.shippingCost || 0;

        return (
          aCarrier.localeCompare(bCarrier) || aShippingCost - bShippingCost
        );
      });

    if (!nodes || nodes?.length <= 0) {
      return (
        <ShippingOptionItem>
          <img
            src={`${market.cdn}/g/rfe/illustrations/package-wings-48px.png`}
            alt="package-icon"
          />
          <TitleWrapper>{missingInfoText}</TitleWrapper>
        </ShippingOptionItem>
      );
    }

    const isInstorePickup = (shipping: Shipping): boolean => {
      return shipping.deliveryMethod === 'INSTORE_PICKUP';
    };

    return nodes?.map((node, i) => {
      const deliveryDays = {
        min: node?.deliveryDays?.min || 0,
        max: node?.deliveryDays?.max || 0,
      };
      return (
        <ShippingOptionItem key={i}>
          {CarrierLogo(node.carrier || '', isInstorePickup(node))}
          <TitleWrapper>
            {node.sustainability === SustainabilityEnum.SUSTAINABLE && (
              <StyledBadge
                color="GREEN"
                icon={IconLeaf}
                size={BadgeSize.SMALL}
                text={t('product', 'shipping.sustainable')}
              />
            )}
            <ItemTitle>
              {!isInstorePickup(node) && (
                <TitleSmallText flex={false}>
                  {getTranslatedShipping(t, deliveryDays)}
                </TitleSmallText>
              )}
            </ItemTitle>
            <BodySmallText>{getDeliveryInfo(node)}</BodySmallText>
          </TitleWrapper>
          <TitleSmallText flex={false}>
            <Currency price={node?.shippingCost} />
          </TitleSmallText>
        </ShippingOptionItem>
      );
    });
  };

  const hasSustainableDeliveryOptions = Boolean(
    offer.shipping?.nodes.find(
      node => node.sustainability === SustainabilityEnum.SUSTAINABLE
    )
  );

  return (
    <>
      <H4Text>{t('store', 'store.delivery-options')}</H4Text>
      {hasSustainableDeliveryOptions && (
        <StyledSustainableDescription>
          <StyledBadge
            color="GREEN"
            icon={IconLeaf}
            size={BadgeSize.SMALL}
            text={t('product', 'shipping.sustainable')}
          />
          <StyledCaptionText emphasis={FontEmphasis.MEDIUM}>
            {t('product', 'shipping.sustainable-description')}
          </StyledCaptionText>
        </StyledSustainableDescription>
      )}
      <ShippingOptionList>{renderShippingOptions()}</ShippingOptionList>
    </>
  );
};
